import { useCallback, useContext, useState } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { DashboardsContext } from '../contexts/DashboardsContext';
import { WorkspacesContext } from '../contexts/WorkspacesContext';
import { addWorkspaceToDashboardFunc, deleteWorkspaceFunc, sendShareWorkspaceEmailFunc, updateWorkspaceFunc } from '../lib/helper';
import { Dashboard, Workspace } from '../types/files';
import { filterInRecord, getFromRecord, removeFromRecord, setInRecord } from '../utils/recordUtils';
import useBackwardsCompatible from './useBackwardsCompatible';

const useBulkWorkspaces = () => {
  const { user, userGroup } = useContext(AuthContext);
  const { dashboards, setDashboards } = useContext(DashboardsContext);
  const { workspacesCollection, setWorkspaces, setWorkspacesCollection } = useContext(WorkspacesContext);
  const { dashboardWorkspaces } = useBackwardsCompatible();
  const [loading, setLoading] = useState<boolean>(false);

  const getBulkWorkspaces = useCallback((collection: string) =>
    getFromRecord(workspacesCollection, collection) ?? []
  , [workspacesCollection]);

  const clearBulk = useCallback((collection?: string) => {
    if (collection)
      setWorkspacesCollection(prev => setInRecord(prev, collection, []));
    else
      setWorkspacesCollection({});
  }, [setWorkspacesCollection]);

  const addToBulk = useCallback((collection: string, workspace: Workspace) => {
    setWorkspacesCollection(prev => {
      const collections = getFromRecord(prev, collection) ?? [];

      if (!collections?.find(item => item.id === workspace.id))
        return setInRecord(prev, collection, [...collections, workspace]);

      return prev;
    });
  }, [setWorkspacesCollection]);

  const removeFromBulk = useCallback((collection: string, workspace: Workspace) => {
    setWorkspacesCollection(prev => {
      const collections = filterInRecord(prev, collection, item => item.id !== workspace.id);
      const remaining = collections[collection];

      return !!remaining?.length ? collections : removeFromRecord(collections, collection);
    });
  }, [setWorkspacesCollection]);

  const assignToDashboards = useCallback((collection: string, dashboardIds: string[]) => {
    return new Promise<Dashboard[] | any>((resolve, reject) => {
      const promises: Promise<Dashboard>[] = [];
      const bulkWorkspaces = getBulkWorkspaces(collection);

      setLoading(true);
      bulkWorkspaces.forEach(workspace => {
        dashboardIds.forEach(id => {
          promises.push(addWorkspaceToDashboardFunc({
            dashboardId: id,
            workspaceId: workspace.id,
            group: userGroup,
          }));
        });
      });

      Promise.all(promises)
        .then(dashboardUpdates => resolve(dashboardUpdates))
        .catch(reject)
        .finally(() => setLoading(false));
    });
  }, [userGroup, getBulkWorkspaces]);

  const shareWorkspaceEmails = useCallback((collection: string) => {
    return new Promise<Dashboard[] | any>((resolve, reject) => {
      const updatePromises: Promise<Workspace>[] = [];
      const sharePromises: Promise<string | any>[] = [];
      const bulkWorkspaces = getBulkWorkspaces(collection);

      setLoading(true);
      bulkWorkspaces.forEach(workspace => {
        if (!workspace.isPublic)
          updatePromises.push(updateWorkspaceFunc({ ...workspace, isPublic: true }));

        const dashboardIds = dashboards.filter(dashboard => dashboardWorkspaces(dashboard)?.includes(workspace.id))
          .map(dashboard => dashboard.id);

        sharePromises.push(sendShareWorkspaceEmailFunc({
          workspaceId: workspace.id,
          toAddress: workspace.email,
          replyToAddress: user.attributes.email,
          dashboardIds: dashboardIds,
        }));
      });

      Promise.all(updatePromises).then(async (updatedWorkspaces?: Workspace[]) => {
        const workspaceIds = updatedWorkspaces?.map(workspace => workspace.id);

        setDashboards(prev => prev.map(dashboard => ({
          ...dashboard,
          isPublic: dashboardWorkspaces(dashboard)?.some(wsId => workspaceIds?.includes(wsId)) || dashboard.isPublic,
        })));
        setWorkspaces(prev => prev.map(ws => updatedWorkspaces?.find(updated => updated.id === ws.id) ?? ws));

        await Promise.all(sharePromises);
        resolve(true);
      }).catch((error) => {
        reject(error);
      }).finally(() => setLoading(false));
    });
  }, [getBulkWorkspaces, dashboards, user, dashboardWorkspaces, setDashboards, setWorkspaces]);

  const deleteWorkspaces = useCallback((collection: string) => {
    return new Promise((resolve, reject) => {
      const promises: Promise<any>[] = [];
      const collections = getBulkWorkspaces(collection);

      setLoading(true);
      collections.forEach(item => promises.push(deleteWorkspaceFunc({ id: item.id })));
      Promise.all(promises).then(() => {
        const workspaceIds = collections.map(item => item.id);

        collections.forEach(workspace => removeFromBulk(collection, workspace));
        setWorkspaces(prev => prev.filter(workspace => !workspaceIds.includes(workspace.id)));
        setLoading(false);

        resolve(true);
      }).catch(() => {
        reject([]);
      }).finally(() => setLoading(false));
    });
  }, [getBulkWorkspaces, removeFromBulk, setLoading, setWorkspaces]);

  return { loading, getBulkWorkspaces, clearBulk, addToBulk, removeFromBulk,
    assignToDashboards, shareWorkspaceEmails, deleteWorkspaces };
};

export default useBulkWorkspaces;